import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Box,
  Container,
  Avatar,
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Dashboard,
  Upload,
  FolderOpen,
  Shield,
  ExitToApp,
} from '@mui/icons-material';
import { useAuth } from 'oidc-react';
import { hasRequiredAccess } from '../../utils/roleHelper';

const Navbar: React.FC = () => {
  const auth = useAuth();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<null | HTMLElement>(null);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMobileMenuAnchor(null);
  };

  const handleLogout = () => {
    auth.signOutRedirect();
    handleMenuClose();
  };

  const isActive = (path: string) => location.pathname === path;

  // Prüfe ob der Benutzer Zugriff auf "Geschützte Steine" hat
  // Prüft Rollen und Gruppen aus ID Token UND Access Token
  const hasProtectedBricksAccess = () => {
    return hasRequiredAccess(
      auth.userData,
      ['website-manager'],
      ['frontend-manager']
    );
  };

  const allMenuItems = [
    { label: 'Dashboard', path: '/dashboard', icon: <Dashboard /> },
    { label: 'Analyse', path: '/upload', icon: <Upload /> },
    { label: 'Dokumente', path: '/documents', icon: <FolderOpen /> },
    { label: 'Geschützte Steine', path: '/protected-bricks', icon: <Shield />, requiresAccess: true },
  ];

  const menuItems = allMenuItems.filter(item =>
    !item.requiresAccess || hasProtectedBricksAccess()
  );

  if (!auth.userData) {
    return null;
  }

  return (
    <AppBar position="sticky" elevation={2} sx={{ bgcolor: 'white', color: 'text.primary' }}>
      <Container maxWidth="xl">
        <Toolbar disableGutters>
          {/* Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
            <img
              src="/ProtectedBricksLogo.svg"
              alt="Protected Bricks Logo"
              style={{ height: '40px', marginRight: '12px' }}
            />
            <Typography
              variant="h6"
              component={Link}
              to="/dashboard"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                textDecoration: 'none',
                display: { xs: 'none', md: 'block' },
              }}
            >
              Protected Bricks
            </Typography>
          </Box>

          {/* Desktop Navigation */}
          <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: 1 }}>
            {menuItems.map((item) => (
              <Button
                key={item.path}
                component={Link}
                to={item.path}
                startIcon={item.icon}
                sx={{
                  color: isActive(item.path) ? 'primary.main' : 'text.secondary',
                  fontWeight: isActive(item.path) ? 600 : 400,
                  borderBottom: isActive(item.path) ? 2 : 0,
                  borderColor: 'primary.main',
                  borderRadius: 0,
                  px: 2,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
          </Box>

          {/* Mobile Menu Icon */}
          <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }} />
          <IconButton
            size="large"
            aria-label="menu"
            aria-controls="mobile-menu"
            aria-haspopup="true"
            onClick={handleMobileMenuOpen}
            sx={{ display: { xs: 'flex', md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          {/* User Profile */}
          <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
            <IconButton
              size="large"
              aria-label="account"
              aria-controls="profile-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                <AccountCircle />
              </Avatar>
            </IconButton>
          </Box>

          {/* Profile Menu */}
          <Menu
            id="profile-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem component={Link} to="/account" onClick={handleMenuClose}>
              <AccountCircle sx={{ mr: 1 }} />
              Mein Account
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ExitToApp sx={{ mr: 1 }} />
              Abmelden
            </MenuItem>
          </Menu>

          {/* Mobile Menu */}
          <Menu
            id="mobile-menu"
            anchorEl={mobileMenuAnchor}
            open={Boolean(mobileMenuAnchor)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {menuItems.map((item) => (
              <MenuItem
                key={item.path}
                component={Link}
                to={item.path}
                onClick={handleMenuClose}
              >
                {item.icon}
                <Typography sx={{ ml: 2 }}>{item.label}</Typography>
              </MenuItem>
            ))}
            <MenuItem component={Link} to="/account" onClick={handleMenuClose}>
              <AccountCircle />
              <Typography sx={{ ml: 2 }}>Mein Account</Typography>
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ExitToApp />
              <Typography sx={{ ml: 2 }}>Abmelden</Typography>
            </MenuItem>
          </Menu>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;
